import styled, { css, keyframes } from 'styled-components';

export type ProgressPhase = 'enter' | 'loading' | 'done';

const indeterminatePulse = keyframes`
  0% { opacity: 0.55; }
  50% { opacity: 1; }
  100% { opacity: 0.55; }
`;

const widthFor = (phase: ProgressPhase) => {
  if (phase === 'enter') {
    return '0%';
  }
  if (phase === 'done') {
    return '100%';
  }
  return '90%';
};

const transitionFor = (phase: ProgressPhase) => {
  if (phase === 'enter') {
    return 'none';
  }
  if (phase === 'done') {
    return 'width 200ms ease-out, opacity 300ms ease-out 120ms';
  }
  // Trickle toward 90% — a long, decelerating ease so it never quite arrives.
  return 'width 8s cubic-bezier(0.08, 0.82, 0.17, 1)';
};

export const ProgressBar = styled.div<{ $phase: ProgressPhase }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 2px;
  z-index: 2000;
  background: ${({ theme }) => theme.token.colorPrimary};
  box-shadow: 0 0 6px ${({ theme }) => theme.token.colorPrimary};
  border-radius: 0 2px 2px 0;
  pointer-events: none;
  will-change: width, opacity;
  width: ${({ $phase }) => widthFor($phase)};
  opacity: ${({ $phase }) => ($phase === 'done' ? 0 : 1)};
  transition: ${({ $phase }) => transitionFor($phase)};

  @media (prefers-reduced-motion: reduce) {
    width: ${({ $phase }) => ($phase === 'enter' ? '0%' : '100%')};
    transition: opacity 150ms linear;
    ${({ $phase }) =>
      $phase === 'loading' &&
      css`
        animation: ${indeterminatePulse} 1.2s ease-in-out infinite;
      `};
  }
`;

/** Visually hidden live region so screen readers hear route changes. */
export const LoadingAnnouncement = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
